import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Table,
  Form,
  Spinner,
  Container,
  Button,
} from "react-bootstrap";
import { AddPatientSteps } from "../PatientsAccounts/AddPatientInfo";
import {
  fetchPractitionersByPractices,
  fetchUserAppointmentsByDate,
  getServiceById,
} from "../../api";
import { jwtDecode } from "jwt-decode";
import { useNavigate } from "react-router-dom";
import { StatusPopover } from "./UpdateStatus";
import "./ListView.css";
import "../../custom.scss";
import CreateReceiptModal from "../Accounts/Receipt";

export default function ListView() {
  const [practitionersByDate, setPractitionersByDate] = useState({});
  const [appointments, setAppointments] = useState([]);
  const [userId, setUserId] = useState(null);
  const [practices, setPractices] = useState([]);
  const [serviceCache, setServiceCache] = useState({});
  const [showPastEventsMap, setShowPastEventsMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [modalShow, setModalShow] = useState(false);
  const [modalData, setModalData] = useState(null);
  const [expandedPractitioners, setExpandedPractitioners] = useState({});
  const [receiptModalShow, setReceiptModalShow] = useState(false);
  const [patientId, setPatientId] = useState(null);

  const [refreshTick, setRefreshTick] = useState(0);
  const navigate = useNavigate();

  const formatToYYYYMMDD = (date) => {
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  useEffect(() => {
    const token = sessionStorage.getItem("user");
    if (!token) {
      console.error("No user token found in session storage.");
      return;
    }
    const decoded = jwtDecode(token);
    setUserId(decoded._id);
    setPractices(decoded.practice || []);
  }, []);

  useEffect(() => {
    const fetchPracticesAndAppointments = async () => {
      if (!userId) return;

      setLoading(true);

      try {
        const practitioners = await fetchPractitionersByPractices(practices);
        setPractices(practitioners);

        const today = formatToYYYYMMDD(new Date());

        const appointmentsResponse = await fetchUserAppointmentsByDate(
          userId,
          today
        );
        setAppointments(appointmentsResponse);

        setPractitionersByDate({ [today]: practitioners });

        // Fetch services in parallel instead of sequentially
        const uncachedServiceIds = [
          ...new Set(appointmentsResponse.map((appt) => appt.serviceId)),
        ].filter((serviceId) => serviceId && !serviceCache[serviceId]);

        if (uncachedServiceIds.length > 0) {
          const servicePromises = uncachedServiceIds.map((serviceId) =>
            getServiceById(serviceId)
              .then((service) => ({ serviceId, name: service.servicesName }))
              .catch(() => ({ serviceId, name: "Unknown Service" }))
          );

          const serviceResults = await Promise.all(servicePromises);

          const newServiceCache = {};
          serviceResults.forEach(({ serviceId, name }) => {
            newServiceCache[serviceId] = name;
          });

          setServiceCache((prevCache) => ({
            ...prevCache,
            ...newServiceCache,
          }));
        }

        setShowPastEventsMap((prevMap) => {
          const newMap = { ...prevMap };
          practitioners.forEach((practitioner) => {
            if (!(practitioner.id in newMap)) {
              newMap[practitioner.id] = false;
            }
          });
          return newMap;
        });

        const initialExpandedState = {};
        practitioners.forEach((practitioner) => {
          initialExpandedState[practitioner.id] = true;
        });
        setExpandedPractitioners(initialExpandedState);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchPracticesAndAppointments();
  }, [userId, refreshTick, serviceCache]);

  const togglePractitioner = (id) => {
    setExpandedPractitioners((prevState) => ({
      ...prevState,
      [id]: !prevState[id],
    }));
  };


  const sortAppointmentsByStartTime = (appointments) => {
    return [...appointments].sort(
      (a, b) => new Date(a.start) - new Date(b.start)
    );
  };

  const handleStatusUpdate = useCallback((appointmentId, newStatus, patientId) => {
    setAppointments((prevAppointments) =>
      prevAppointments.map((appt) =>
        appt._id === appointmentId ? { ...appt, status: newStatus } : appt
      )
    );

    if (newStatus === "Completed") {
      navigate(`/patients/appointment/${patientId}/1`, { state: { collapsed: true } });
    }
  }, [navigate]);

  const renderStatusIcon = useCallback((status, appointmentId, appointment) => {
    if (!appointment) return null;

    return (
      <StatusPopover
        appointmentId={appointmentId}
        patientId={appointment.patientId}
        currentStatus={status}
        onUpdate={(newStatus) =>
          handleStatusUpdate(appointmentId, newStatus, appointment.patientId)
        }
      />
    );
  }, [handleStatusUpdate]);

  // Memoize filtered and sorted appointments by practitioner
  const appointmentsByPractitioner = useMemo(() => {
    const currentTime = new Date();
    const twoHoursAgo = new Date(currentTime.getTime() - 2 * 60 * 60 * 1000);
    const today = formatToYYYYMMDD(new Date());

    const result = {};

    appointments.forEach((appt) => {
      if (!appt.start.startsWith(today)) return;

      const practitionerId = appt.resourceId;
      if (!result[practitionerId]) {
        result[practitionerId] = {
          past: [],
          current: [],
        };
      }

      const endTime = new Date(appt.end);
      if (endTime < twoHoursAgo) {
        result[practitionerId].past.push(appt);
      } else {
        result[practitionerId].current.push(appt);
      }
    });

    // Sort appointments for each practitioner
    Object.keys(result).forEach((practitionerId) => {
      result[practitionerId].past = sortAppointmentsByStartTime(
        result[practitionerId].past
      );
      result[practitionerId].current = sortAppointmentsByStartTime(
        result[practitionerId].current
      );
    });

    return result;
  }, [appointments]);

  const renderTable = useCallback((practitioner, date) => {
    const showPastEvents = showPastEventsMap[practitioner.id] || false;

    const practitionerAppts = appointmentsByPractitioner[practitioner.id] || {
      past: [],
      current: [],
    };

    const sortedPastAppointments = practitionerAppts.past;
    const sortedCurrentAppointments = practitionerAppts.current;

    const handleDescriptionCellClick = (appointment) => {
      if (appointment.status === "New Patient") {
        setModalData({
          firstName: appointment.patientFirstName || "Unknown",
          lastName: appointment.patientLastName || "Unknown",
          email: appointment.patientEmail || "Unknown",
          appointmentId: appointment._id,
        });
        setModalShow(true);
        return;
      }

      if (appointment.status === "Cancelled") {
        // optionally: show a toast/tooltip here
        return; // 🚫 block navigation
      }
      if (appointment.status === "Completed") {
        navigate(
          `/patients/appointment/${appointment.patientId}/1`
        );
      } else {
        navigate(
          `/patients/appointment/${appointment.patientId}/${appointment._id}`
        );
      }

    };

    return (
      <div className="mb-0">
        {sortedPastAppointments.length > 0 && (
          <Form.Check
            className="d-flex justify-content-start mb-2"
            type="switch"
            id={`toggle-past-events-${practitioner.id}`}
            label={
              <span style={{ marginLeft: "10px" }}>
                {showPastEvents ? "Hide Earlier" : "Earlier"}
              </span>
            }
            checked={showPastEvents}
            onChange={() =>
              setShowPastEventsMap((prev) => ({
                ...prev,
                [practitioner.id]: !prev[practitioner.id],
              }))
            }
          />
        )}
        {showPastEvents && sortedPastAppointments.length > 0 && (
          <Table className="ms-1 me-1">
            <tbody>
              {sortedPastAppointments.map((appointment) => {
                const isNewPatient = appointment.status === "New Patient";
                const isCancelled = appointment.status === "Cancelled";
                const isScheduling = appointment.status === "Scheduling";
                const isNoPatient = !appointment.patientId && !isNewPatient;
                const isBlocked = isCancelled || isScheduling || isNoPatient;

                const timeRange = `${new Date(
                  appointment.start
                ).toLocaleTimeString([], {
                  hour: "2-digit",
                  minute: "2-digit",
                })} - ${new Date(appointment.end).toLocaleTimeString([], {
                  hour: "2-digit",
                  minute: "2-digit",
                })}`;

                return (
                  <tr key={appointment._id}>
                    <td style={{ width: "15%" }}>{timeRange}</td>

                    <td
                      style={{ width: "5%" }}
                      className="display-flex align-items-center justify-content-center"
                    >
                      {renderStatusIcon(
                        appointment.status,
                        appointment._id,
                        appointment
                      )}
                    </td>

                    <td
                      onClick={
                        !isBlocked
                          ? () => handleDescriptionCellClick(appointment)
                          : undefined
                      }
                      style={{
                        cursor: isBlocked ? "default" : "pointer",
                        width: "80%",
                        opacity: isCancelled || isNoPatient ? 0.6 : 1,
                      }}
                      title={
                        isNewPatient
                          ? "Complete patient details first"
                          : isCancelled
                          ? "Cancelled appointment — cannot open"
                          : isScheduling
                          ? "Scheduling in progress"
                          : isNoPatient
                          ? "Awaiting patient intake — not yet linked"
                          : ""
                      }
                      className={isBlocked ? "text-muted" : ""}
                    >
                      {`${appointment.title}${
                        appointment.serviceId
                          ? ` - ${serviceCache[appointment.serviceId] || "Loading..."}`
                          : ""
                      }`}
                    </td>

                    <td style={{ width: "5%" }} className="text-end">
                      {appointment.status === "Completed" && ( // 👈 only for Completed
                        <Button
                          variant="outline-primary"
                          className="d-flex align-items-center justify-content-center gap-2"
                          onClick={() => setReceiptModalShow(true)}
                          title="Open receipt"
                        >
                          <i className="fa-solid fa-receipt text-primary"></i>
                        </Button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        )}

        <Table className="ms-1 me-1">
          <tbody>
            {sortedCurrentAppointments.map((appointment) => {
              const isNewPatient = appointment.status === "New Patient";
              const isCancelled = appointment.status === "Cancelled";
              const isScheduling = appointment.status === "Scheduling";
              const isNoPatient = !appointment.patientId && !isNewPatient;
              const isBlocked = isCancelled || isScheduling || isNoPatient;

              const timeRange = `${new Date(
                appointment.start
              ).toLocaleTimeString([], {
                hour: "2-digit",
                minute: "2-digit",
              })} - ${new Date(appointment.end).toLocaleTimeString([], {
                hour: "2-digit",
                minute: "2-digit",
              })}`;

              return (
                <tr key={appointment._id}>
                  <td style={{ width: "15%" }}>{timeRange}</td>

                  <td style={{ width: "5%" }}>
                    {renderStatusIcon(
                      appointment.status,
                      appointment._id,
                      appointment
                    )}
                  </td>

                  <td
                    onClick={
                      !isBlocked
                        ? () => handleDescriptionCellClick(appointment)
                        : undefined
                    }
                    style={{
                      cursor: isBlocked ? "default" : "pointer",
                      width: "80%",
                      opacity: isCancelled || isNoPatient ? 0.6 : 1,
                    }}
                    title={
                      isNewPatient
                        ? "Complete patient details first"
                        : isCancelled
                        ? "Cancelled appointment — cannot open"
                        : isScheduling
                        ? "Scheduling in progress"
                        : isNoPatient
                        ? "Awaiting patient intake — not yet linked"
                        : ""
                    }
                    className={isBlocked ? "text-muted" : ""}
                  >
                    {`${appointment.title}${
                      appointment.serviceId
                        ? ` - ${serviceCache[appointment.serviceId] || "Loading..."}`
                        : ""
                    }`}
                  </td>

                  <td style={{ width: "5%" }} className="text-end">
                    {appointment.status === "Completed" && ( // 👈 only for Completed
                      <Button
                        variant=""
                        size="lg"
                        className="d-flex rounded-0 align-items-center justify-content-center gap-2"
                        onClick={() => {
                          setReceiptModalShow(true);
                          setPatientId(appointment.patientId); // Set patient ID for receipt
                        }}
                        title="Open receipt"
                      >
                        <i className="fa-solid fa-receipt text-primary"></i>
                      </Button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      </div>
    );
  }, [
    appointmentsByPractitioner,
    showPastEventsMap,
    serviceCache,
    navigate,
    setModalData,
    setModalShow,
    setReceiptModalShow,
    setPatientId,
  ]);

  const today = new Date().toISOString().split("T")[0];

  const renderAccordion = useMemo(() => {
    const today = Object.keys(practitionersByDate)[0];
    const practitioners = practitionersByDate[today] || [];

    return (
      <Container>
        {practitioners.length > 0 ? (
          practitioners.map((practitioner) => {
            const isExpanded = expandedPractitioners[practitioner.id];
            return (
              <div key={practitioner.id} className="mb-3 w-100">
                <div
                  className={`d-flex justify-content-between align-items-center px-3 py-2 ${
                    isExpanded ? "bgheader" : "bg-light text-primary"
                  }`}
                  style={{ borderBottom: "1px solid #ccc" }}
                >
                  <span>{practitioner.title}</span>
                  <Button
                    variant="link"
                    size="sm"
                    onClick={() => togglePractitioner(practitioner.id)}
                    className={`p-0 ${
                      isExpanded ? "bgheader" : "text-primary"
                    }`}
                  >
                    {isExpanded ? (
                      <i className="fa-solid fa-angle-up"></i>
                    ) : (
                      <i className="fa-solid fa-angle-down"></i>
                    )}
                  </Button>
                </div>
                {isExpanded && (
                  <Container className="mt-2 ms-2 table-responsive">
                    {renderTable(practitioner, today)}
                  </Container>
                )}
              </div>
            );
          })
        ) : (
          // Show message when no practitioners are found
          <div className="text-center text-secondary mt-3">
            <h6 className="fw-light">No appointments scheduled for today</h6>
          </div>
        )}
      </Container>
    );
  }, [
    practitionersByDate,
    expandedPractitioners,
    renderTable,
  ]);

  return (
    <div className="pt-3">
      {loading ? (
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "50vh" }}>
          <Spinner animation="border" role="status" variant="primary">
            <span className="visually-hidden">Loading appointments...</span>
          </Spinner>
        </div>
      ) : (
        <>
          {renderAccordion}
          {modalShow && (
            <AddPatientSteps
              show={modalShow}
              onHide={() => setModalShow(false)}
              patientFirstName={modalData?.firstName}
              patientLastName={modalData?.lastName}
              patientEmail={modalData?.email}
              appointmentId={modalData?.appointmentId}
              onSaved={() => {
                setModalShow(false);
                setRefreshTick((t) => t + 1); // 🔁 triggers re-fetch
              }}
            />
          )}
        </>
      )}
      {receiptModalShow && (
        <CreateReceiptModal
          show={receiptModalShow}
          onHide={() => setReceiptModalShow(false)}
          patientId={patientId}
        />
      )}
    </div>
  );
}
